import { createContext, useContext, useState } from "react";
import {
  clients as seedClients,
  tasks as seedTasks,
  orders as seedOrders,
  documents as seedDocs,
} from "@/data/mockData";

const DataContext = createContext(null);

function uid(prefix) {
  return `${prefix}${Math.random().toString(36).slice(2, 8)}${Date.now().toString(36).slice(-3)}`;
}

export function DataProvider({ children }) {
  const [clients, setClients] = useState(seedClients);
  const [tasks, setTasks] = useState(seedTasks);
  const [orders, setOrders] = useState(seedOrders);
  const [documents, setDocuments] = useState(seedDocs);

  // Clients
  const addClient = (c) =>
    setClients((arr) => [
      {
        id: uid("c"),
        notes: "",
        phone: "",
        company: "",
        email: "",
        status: "Active",
        added: new Date().toISOString().slice(0, 10),
        tasks: [],
        orders: [],
        documents: [],
        ...c,
      },
      ...arr,
    ]);
  const updateClient = (id, patch) =>
    setClients((arr) => arr.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const deleteClient = (id) =>
    setClients((arr) => arr.filter((c) => c.id !== id));

  // Tasks
  const addTask = (t) =>
    setTasks((arr) => [
      {
        id: uid("t"),
        status: "Pending",
        priority: "Medium",
        due: "No due date",
        client: "",
        done: false,
        ...t,
      },
      ...arr,
    ]);
  const updateTask = (id, patch) =>
    setTasks((arr) => arr.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const deleteTask = (id) => setTasks((arr) => arr.filter((t) => t.id !== id));

  // Orders
  const nextOrderNumber = () => {
    const nums = orders
      .map((o) => parseInt(String(o.number).replace(/\D/g, ""), 10) || 0);
    return `#${(Math.max(100, ...nums) + 1)}`;
  };
  const addOrder = (o) =>
    setOrders((arr) => [
      {
        id: uid("o"),
        number: nextOrderNumber(),
        status: "In Progress",
        date: new Date().toISOString().slice(0, 10),
        amount: "$0.00",
        client: "",
        ...o,
      },
      ...arr,
    ]);
  const updateOrder = (id, patch) =>
    setOrders((arr) => arr.map((o) => (o.id === id ? { ...o, ...patch } : o)));
  const deleteOrder = (id) =>
    setOrders((arr) => arr.filter((o) => o.id !== id));

  // Documents
  const addDocument = (d) =>
    setDocuments((arr) => [
      {
        id: uid("d"),
        size: "—",
        type: "FILE",
        uploaded: new Date().toISOString().slice(0, 10),
        ...d,
      },
      ...arr,
    ]);
  const deleteDocument = (id) =>
    setDocuments((arr) => arr.filter((d) => d.id !== id));

  return (
    <DataContext.Provider
      value={{
        clients,
        tasks,
        orders,
        documents,
        addClient,
        updateClient,
        deleteClient,
        addTask,
        updateTask,
        deleteTask,
        addOrder,
        updateOrder,
        deleteOrder,
        addDocument,
        deleteDocument,
      }}
    >
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used inside DataProvider");
  return ctx;
}
